import { generateUuidV4 } from '../../util/uuid';
import { apollo_client, BatchWrite } from '../app/db_util';
import { MetadataError } from '@ep/error-handling';
import { EntityLabel, ReportingPeriod, ReportingPeriodGroup } from '@esg/esg-global-types';
import { DocumentReference } from 'firebase/firestore';
import { auth } from '../google/firebase';
import moment, { Moment } from 'moment';
import { GET_REPORTING_PERIOD_GROUPS_QUERY } from '../graphql/queries/metric_capture/reporting_period_group';
import {
  ASSIGN_REPORTING_PERIOD_GROUP_MUTATOR,
  CREATE_REPORTING_PERIOD_GROUP_MUTATOR,
  DELETE_REPORTING_PERIOD_GROUP_MUTATOR,
  UPDATE_REPORTING_PERIOD_GROUP_MUTATOR
} from '../graphql/mutations/metric_capture/reporting_period_group';

export interface ReportingPeriodGroupExtended extends ReportingPeriodGroup {
  reporting_periods: Array<ReportingPeriod>;
}

// Singular and plural label for model entity.
export const reporting_period_group_label: EntityLabel = {
  one: 'Reporting Period Group',
  many: 'Reporting Period Groups'
};

/**
 * Function to query all reporting period groups for a given company
 * @param {string} group_id Group to query reporting period groups for
 * @param {string} company_id Company to query reporting period groups for
 * @returns {Array<ReportingPeriodGroup>}
 */
export const getReportingPeriodGroups = async (group_id: string, company_id: string) => {
  try {
    const reporting_period_groups: Array<ReportingPeriodGroup> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id
        },
        query: GET_REPORTING_PERIOD_GROUPS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.reporting_period_groups;
      });
    return reporting_period_groups;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling getReportingPeriodGroups.',
      {
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Function to create Reporting Period Group with relative data
 * @param {string} group_id ID of Group which Reporting Period Group is being created for
 * @param {string} company_id ID of Company which Reporting Period Group is being created for
 * @param {string} reporting_period_group_name Name of new Reporting Period Group
 * @param {Array<ReportingPeriod>} assigned_reporting_periods List of reporting periods to assign to new reporting period group
 * @returns {DocumentReference}
 */
export const createReportingPeriodGroup = async (
  group_id: string,
  company_id: string,
  reporting_period_group_name: string,
  assigned_reporting_periods: Array<ReportingPeriod>
) => {
  try {
    const new_reporting_period_group: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_REPORTING_PERIOD_GROUP_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          reporting_period_group_name: reporting_period_group_name,
          assigned_reporting_periods: assigned_reporting_periods,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.createReportingPeriodGroup;
      });
    return new_reporting_period_group;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling createReportingPeriodGroup.',
      {
        group_id: group_id,
        company_id: company_id,
        reporting_period_group_name: reporting_period_group_name,
        assigned_reporting_periods: assigned_reporting_periods
      },
      tracking_id
    );
  }
};

/**
 * Function to update reporting period group with relative data
 * @param {string} group_id ID of Group to update reporting period group for
 * @param {string} company_id ID of Company to update reporting period group for
 * @param {ReportingPeriodGroup} updated_reporting_period_group New Reporting Period Group data to push into document
 * @returns {DocumentReference}
 */
export const updateReportingPeriodGroup = async (
  group_id: string,
  company_id: string,
  updated_reporting_period_group: ReportingPeriodGroupExtended,
  original_reporting_period_group: ReportingPeriodGroupExtended
) => {
  try {
    const updated_reporting_period_group_doc: DocumentReference = await apollo_client
      .mutate({
        mutation: UPDATE_REPORTING_PERIOD_GROUP_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          updated_reporting_period_group: updated_reporting_period_group,
          original_reporting_period_group: original_reporting_period_group,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.updateReportingPeriodGroup;
      });
    return updated_reporting_period_group_doc;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling updateReportingPeriodGroup.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_reporting_period_group: updated_reporting_period_group,
        original_reporting_period_group: original_reporting_period_group
      },
      tracking_id
    );
  }
};

/**
 * Function to delete Reporting Period Group
 * @param {string} group_id ID of Group to delete Reporting Period Group for
 * @param {string} company_id ID of Company to delete Reporting Period Group for
 * @param {string} reporting_period_group_id ID of Reporting Period Group to delete
 * @param {Array<ReportingPeriod>} unassigned_reporting_periods Reporting Periods which will be unassigned after reporting period group is deleted
 * @returns {void}
 */
export const deleteReportingPeriodGroup = async (
  group_id: string,
  company_id: string,
  reporting_period_group_id: string,
  reporting_period_group_name: string,
  unassigned_reporting_periods: Array<ReportingPeriod>
) => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_REPORTING_PERIOD_GROUP_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          reporting_period_group_id: reporting_period_group_id,
          reporting_period_group_name: reporting_period_group_name,
          unassigned_reporting_periods: unassigned_reporting_periods,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteReportingPeriodGroup;
      });
    return;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling deleteReportingPeriodGroup.',
      {
        group_id: group_id,
        company_id: company_id,
        reporting_period_group_id: reporting_period_group_id,
        reporting_period_group_name: reporting_period_group_name,
        unassigned_reporting_periods: unassigned_reporting_periods
      },
      tracking_id
    );
  }
};

/**
 * Function to assign or unassign a given Reporting Period Group to a list of Reporting Periods
 * @param {string} group_id ID of Group containing Reporting Periods
 * @param {string} company_id ID of Company containing Reporting Periods
 * @param {string | undefined} reporting_period_group_id ID of Reporting Period Groups to assign to Reporting Periods if present or unassign if not
 * @param {Array<ReportingPeriod>} reporting_periods List of Reporting Periods being updated
 * @returns {Promise<Array<BatchWrite>>}
 */
export const assignReportingPeriodGroup = async (
  group_id: string,
  company_id: string,
  reporting_periods: Array<ReportingPeriod>,
  reporting_period_group_id?: string
) => {
  try {
    const reporting_period_group_writes: Array<BatchWrite> = await apollo_client
      .mutate({
        mutation: ASSIGN_REPORTING_PERIOD_GROUP_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          reporting_periods: reporting_periods,
          reporting_period_group_id: reporting_period_group_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.assignReportingPeriodGroup;
      });
    return reporting_period_group_writes;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling assignReportingPeriodGroup.',
      {
        group_id: group_id,
        company_id: company_id,
        reporting_periods: reporting_periods,
        reporting_period_group_id: reporting_period_group_id
      },
      tracking_id
    );
  }
};

/**
 * Function to append a list of assigned reporting periods to each reporting period group object's data
 * @param {Array<ReportingPeriodGroup>} reporting_period_groups List of Reporting Period Groups to append to
 * @param {Array<ReportingPeriod>} reporting_periods List of reporting periods to reference per group
 * @returns {Array<ReportingPeriodGroupExtended>}
 */
export const joinReportingPeriodGroupsReportingPeriods = (
  reporting_period_groups: Array<ReportingPeriodGroup>,
  reporting_periods: Array<ReportingPeriod>
) => {
  const joined_reporting_period_groups: Array<ReportingPeriodGroupExtended> =
    reporting_period_groups.map((reporting_period_group: ReportingPeriodGroup) => {
      return {
        ...reporting_period_group,
        reporting_periods: reporting_periods.filter((reporting_period: ReportingPeriod) => {
          return reporting_period.reporting_period_group?.id === reporting_period_group.id;
        })
      };
    });
  return joined_reporting_period_groups;
};

/**
 * Function to find the lowest start date in a group of reporting periods
 * @param {Array<ReportingPeriodGroup>} reporting_period_groups List of Reporting Period Groups to append to
 * @param {Array<ReportingPeriod>} reporting_periods List of reporting periods to reference per group
 * @param {ReportingPeriodGroup} test_group Current group you want to find lowest start date for
 * @returns {Moment}
 */
export const findLowestStartDateInGroup = (
  reporting_period_groups: Array<ReportingPeriodGroup>,
  reporting_periods: Array<ReportingPeriod>,
  test_group: ReportingPeriodGroup
) => {
  try {
    const joined_reporting_group: Array<ReportingPeriodGroupExtended> =
      joinReportingPeriodGroupsReportingPeriods(reporting_period_groups, reporting_periods).filter(
        (joined_data) => {
          return joined_data.id === test_group.id;
        }
      );

    // Begin with extremely high start date to ensure that first reporting period start date parsed must be lower.
    let lowest_start: Moment = moment('9999-01-01');
    !!joined_reporting_group[0] &&
      joined_reporting_group[0].reporting_periods.forEach((reporting_period) => {
        if (reporting_period.start < lowest_start) {
          lowest_start = reporting_period.start;
        }
      });
    return lowest_start;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling findLowestSTartDateInGroup.',
      {
        reporting_period_groups: reporting_period_groups,
        reporting_periods: reporting_periods,
        test_group: test_group
      },
      tracking_id
    );
  }
};

/**
 * Function to find the highest end date in a group of reporting periods
 * @param {Array<ReportingPeriodGroup>} reporting_period_groups List of Reporting Period Groups to append to
 * @param {Array<ReportingPeriod>} reporting_periods List of reporting periods to reference per group
 * @param {ReportingPeriodGroup} test_group Current group you want to find lowest start date for
 * @returns {Moment}
 */

export const findHighestEndDateInGroup = (
  reporting_period_groups: Array<ReportingPeriodGroup>,
  reporting_periods: Array<ReportingPeriod>,
  test_group: ReportingPeriodGroup
) => {
  try {
    const joined_reporting_group: Array<ReportingPeriodGroupExtended> =
      joinReportingPeriodGroupsReportingPeriods(reporting_period_groups, reporting_periods).filter(
        (joined_data) => {
          return joined_data.id === test_group.id;
        }
      );
    let highest_end: Moment = moment('1970-01-01');
    !!joined_reporting_group[0] &&
      joined_reporting_group[0].reporting_periods.forEach((reporting_period) => {
        if (reporting_period.end > highest_end) {
          highest_end = reporting_period.end;
        }
      });
    return highest_end;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period_group.ts failed on an unknown error while calling findHighestEndDateInGroup.',
      {
        reporting_period_groups: reporting_period_groups,
        reporting_periods: reporting_periods,
        test_group: test_group
      },
      tracking_id
    );
  }
};

/**
 * Returns a label for the latest reporting period.
 * @param {Array<ReportingPeriod> | ReportingPeriod | null} reporting_periods One or more reporting periods, or null.
 * @returns {string} A string like "FY25" representing the fiscal year, or an empty string if input is invalid.
 */
export const getLatestReportingPeriodGroupYearLabel = (
  reporting_periods: Array<ReportingPeriod> | ReportingPeriod | null
): string => {
  if (!reporting_periods) return '';

  // Normalize to array for consistent processing.
  const reporting_periods_normalized: Array<ReportingPeriod> | ReportingPeriod = Array.isArray(
    reporting_periods
  )
    ? reporting_periods
    : [reporting_periods];

  if (!reporting_periods_normalized.length) return '';

  // Find the latest reporting period based on the end date.
  const latest_reporting_period: ReportingPeriod = reporting_periods_normalized.reduce(
    (latest, current) => (moment(current.end).isAfter(moment(latest.end)) ? current : latest)
  );

  // Format the year as two-digit fiscal year.
  const reporting_period_group_year_label: string = moment(latest_reporting_period.end).format(
    'YY'
  );

  return `FY${reporting_period_group_year_label}`;
};
